import React, { useEffect, useState } from 'react';
import {
  listAlerts, acknowledgeAlert, resolveAlert, reopenAlert, listPatients,
} from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useCanManageAlerts, familyCanActOn, isAdminUser } from '../lib/familyAlerts';
import { isMaintenance } from '../lib/alertText';
import PaginationFooter from '../components/PaginationFooter';
import LastUpdated from '../components/LastUpdated';
import { AlertDrawer } from '../components/AlertDrawer';
import { AlertCard } from '../components/AlertCard';
import { Checks, Heartbeat, Wrench } from '@phosphor-icons/react';
import { toast } from 'sonner';

const FILTERS = ['new', 'acknowledged', 'resolved', 'all'];

export default function Alerts() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canManage = useCanManageAlerts();
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('new');
  const [view, setView] = useState('care'); // care | maintenance
  const [patients, setPatients] = useState({});
  const [total, setTotal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const PAGE_SIZE = 50;

  useEffect(() => {
    listPatients().then(({ items }) => {
      const map = {};
      items.forEach((p) => { map[p.id] = p; });
      setPatients(map);
    });
  }, []);

  const buildParams = (offset) => {
    const params = { limit: PAGE_SIZE, offset };
    if (filter !== 'all') params.status = filter; // backend filters by status
    return params;
  };

  const reqRef = React.useRef(0);
  const load = async () => {
    const req = ++reqRef.current;
    const { items, total: tot } = await listAlerts(buildParams(0));
    if (req !== reqRef.current) return; // a newer filter's response wins
    setAlerts(items);
    setTotal(tot);
    setLastRefresh(Date.now());
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const req = reqRef.current;
    try {
      // listAlerts returns a parsed shape; we fall back to raw API for headers compatibility
      const { items } = await listAlerts({ ...buildParams(alerts.length) });
      if (req === reqRef.current) setAlerts((prev) => [...prev, ...items]);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      if (alerts.length > PAGE_SIZE) return;
      load();
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [filter, alerts.length]);

  const ack = async (id) => {
    try {
      await acknowledgeAlert(id);
      toast.success(t('acknowledged'));
      load();
    } catch { toast.error(t('generic_error')); }
  };

  const [ackAllBusy, setAckAllBusy] = useState(false);
  const shown = alerts.filter((a) => (view === 'maintenance') === isMaintenance(a));
  const maintCount = alerts.filter(isMaintenance).length;
  const unacked = shown.filter((a) => !a.acknowledged && a.status === 'new'
    && (isAdminUser(user) || familyCanActOn(a)));
  const ackAll = async () => {
    if (unacked.length === 0 || ackAllBusy) return;
    if (!window.confirm(t('ack_all_confirm', { count: unacked.length }))) return;
    setAckAllBusy(true);
    let ok = 0, fail = 0;
    // Sequential to keep backend pressure low and so the toast counter is honest.
    for (const a of unacked) {
      try { await acknowledgeAlert(a.id); ok++; }
      catch { fail++; }
    }
    setAckAllBusy(false);
    if (fail === 0) toast.success(t('ack_all_done', { count: ok }));
    else toast.warning(t('ack_all_partial', { ok, fail }));
    load();
  };

  const resolve = async (id) => {
    try {
      await resolveAlert(id);
      toast.success(t('status_resolved'));
      load();
    } catch { toast.error(t('generic_error')); }
  };

  const reopen = async (id) => {
    try {
      await reopenAlert(id);
      toast.success(t('reopened'));
      load();
    } catch { toast.error(t('generic_error')); }
  };

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
            {t('nav_alerts')}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('alerts_title')}</h1>
          <p className="text-ink-soft mt-3 max-w-xl">{t('alerts_sub')}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canManage && unacked.length > 0 && (
            <button
              type="button"
              onClick={ackAll}
              disabled={ackAllBusy}
              data-testid="alerts-ack-all"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50 transition-colors"
            >
              <Checks size={16} weight="bold" />
              {ackAllBusy ? t('ack_all_running') : `${t('ack_all')} (${unacked.length})`}
            </button>
          )}
          <span
            data-testid="alerts-autorefresh-live"
            className="inline-flex flex-col items-end gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-sage-700 bg-sage-100 px-3 py-1.5 rounded-2xl"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-600 animate-pulse" />
              {t('auto_refresh')}
            </span>
            <LastUpdated at={lastRefresh} testid="alerts-last-updated" />
          </span>
          <div className="flex gap-1 bg-white border border-stone_line rounded-xl p-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`alerts-filter-${f}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === f ? 'bg-sage text-white' : 'text-ink-soft hover:bg-bg-alt'
                }`}
              >
                {f === 'all' ? t('all') : t(`status_${f}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="flex gap-1 border-b border-stone_line" role="tablist" data-testid="alerts-view-tabs">
          {[['care', Heartbeat, alerts.length - maintCount], ['maintenance', Wrench, maintCount]].map(([v, Icon, n]) => (
            <button
              key={v}
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              data-testid={`alerts-view-${v}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${view === v ? 'border-sage text-ink' : 'border-transparent text-ink-soft hover:text-ink'}`}
            >
              <Icon size={16} weight="duotone" />
              {t(v === 'care' ? 'al_tab_care' : 'al_tab_maint')}
              <span className="text-xs text-ink-muted tabular-nums" data-testid={`alerts-view-${v}-count`}>{n}</span>
            </button>
          ))}
      </div>
      {view === 'maintenance' && <p className="text-sm text-ink-soft -mt-4">{t('al_maint_hint')}</p>}

      {shown.length === 0 ? (
        <div className="bg-white border border-stone_line rounded-2xl p-12 text-center">
          <div className="text-ink-soft">{t(view === 'maintenance' ? 'al_maint_empty' : 'no_alerts')}</div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="alerts-list">
          {shown.map((a) => (
            <AlertCard
              key={a.id}
              alert={a}
              patient={patients[a.resident_uuid]}
              onAck={ack}
              onResolve={resolve}
              onReopen={reopen}
              onOpenTimeline={setDrawerId}
              onNoteAdded={load}
            />
          ))}
        </ul>
      )}

      {alerts.length > 0 && (
        <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
          <PaginationFooter
            shown={alerts.length}
            total={total}
            onLoadMore={loadMore}
            loading={loadingMore}
            testid="alerts-pagination"
          />
        </div>
      )}

      {drawerId && (
        <AlertDrawer
          alertId={drawerId}
          alert={alerts.find((a) => a.id === drawerId) || null}
          onClose={() => setDrawerId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
